import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Box, Button, Select, Heading, useToast } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const SetQuestionCategory = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [questions, setQuestions] = useState([]);
  const gridRef = useRef();
  const [gridApi, setGridApi] = useState(null);
  const toast = useToast();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/categories`);
        setCategories(response.data.$values);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchQuestionsWithoutCategory = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/QuestionCategories/without-category`);
        setQuestions(response.data.$values);
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };

    fetchQuestionsWithoutCategory();
  }, []);

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleSave = async () => {
    if (!selectedCategory) {
      toast({
        title: "Error",
        description: "Please select a category first",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const selectedNodes = gridApi.getSelectedNodes();
    const selectedQuestions = selectedNodes.map((node) => node.data.questionId);

    if (selectedQuestions.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one question",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/QuestionCategories/change-category`, {
        questionIds: selectedQuestions,
        newCategoryId: selectedCategory,
      });

      toast({
        title: "Success",
        description: "Questions moved successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      setQuestions(questions.filter((q) => !selectedQuestions.includes(q.questionId)));
    } catch (error) {
      console.error('Error moving questions:', error);
      toast({
        title: "Error",
        description: "An error occurred while moving questions",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    gridRef.current = params.api;
  };

  const onSelectionChanged = useCallback(() => {
    const selectedNodes = gridRef.current.getSelectedRows();
    console.log('Selected Nodes:', selectedNodes);
  }, []);

  const columnDefs = useMemo(() => [
    { headerName: 'Question ID', field: 'questionId', sortable: true, filter: true, hide: true },
    { headerName: 'Question Text', field: 'questionText', sortable: true, filter: true },
    { headerName: 'Select', field: 'questionId', checkboxSelection: true },
  ], []);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    minWidth: 100,
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: true,
  }), []);

  return (
    <Box p={8}>
      <Heading mb={6}>Set Question Category</Heading>
      <Select placeholder="Select Category" onChange={handleCategoryChange} mb={4}>
        {categories.map((category) => (
          <option key={category.categoryId} value={category.categoryId}>
            {category.categoryName}
          </option>
        ))}
      </Select>
      <Button onClick={handleSave} colorScheme="blue" mb={4}>Submit</Button>
      <div className="ag-theme-alpine" style={{ height: questions.length > 0 ? '600px' : '200px', width: '100%' }}>
        <AgGridReact
          columnDefs={columnDefs}
          rowData={questions}
          rowSelection="multiple"
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChanged}
          domLayout="autoHeight"
          pagination={true}
          paginationPageSize={10}
          defaultColDef={defaultColDef}
        />
      </div>
    </Box>
  );
};

export default SetQuestionCategory;
